import { useState } from 'react'

import { useMutation } from '@apollo/client'
import {
  Box,
  BoxList,
  Button,
  IconBadge,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  ArchiveTaxonomiesDocument,
  ContractsSettingPageDocument,
  CreateTaxonomiesDocument,
  GetLegalEntityContractSettingsDocument,
} from 'generated/resources/graphql'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { ContractFieldsProps } from '../types'
import EditFieldModal from './EditFieldModal'

type IconType = 'pin' | 'lock-locked'

type Props = {
  name: string
  values?: ContractFieldsProps[]
  originalKey: string
  contractsTemplateId: number
  tag?: {
    icon: IconType
    label: string
    hover: string
  }
  legalEntityId?: number
}

type query = typeof GetLegalEntityContractSettingsDocument | typeof ContractsSettingPageDocument
type queryVariables = {
  companyId: number
  legalEntityId?: number
}

const mutationsVariables = {
  frenchContractType: false,
  frenchLevel: false,
  frenchMutual: false,
  frenchProfessionalCategory: false,
  frenchStep: false,
  frenchWorkType: false,
  germanContractType: false,
  portugueseContractType: false,
  spanishContractType: false,
  spanishEducationLevel: false,
  spanishProfessionalCategory: false,
  spanishWorkingDayType: false,
  compensations: false,
}

const ContractField = ({
  name,
  values,
  originalKey,
  contractsTemplateId,
  tag,
  legalEntityId,
}: Props) => {
  const { company } = useCurrentSession()
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [options, setOptions] = useState<ContractFieldsProps[]>(values || [])
  const [open, toggle] = useToggle()
  const mutationVariables = { ...mutationsVariables, [originalKey]: true }

  const fetchQueries: { query: query; variables: queryVariables }[] = [
    {
      query: ContractsSettingPageDocument,
      variables: {
        companyId: company.id,
      },
    },
  ]

  if (legalEntityId) {
    fetchQueries.push({
      query: GetLegalEntityContractSettingsDocument,
      variables: {
        companyId: company.id,
        legalEntityId: legalEntityId,
      },
    })
  }

  const [archiveTaxonomies] = useMutation(ArchiveTaxonomiesDocument, {
    awaitRefetchQueries: true,
    refetchQueries: fetchQueries,
  })
  const [createTaxonomies] = useMutation(CreateTaxonomiesDocument, {
    awaitRefetchQueries: true,
    refetchQueries: fetchQueries,
  })

  useUnsafeEffect(() => {
    if (values) {
      sortOptions(values)
    }
  }, [values])

  const sortOptions = (val: ContractFieldsProps[]) => {
    const sortedOptions = [...val].sort((a, b) => {
      if (a.new && !b.new) return 1
      if (!a.new && b.new) return -1
      if (a.archivedAt && !b.archivedAt) return 1
      if (!a.archivedAt && b.archivedAt) return -1
      if (!a.new && !b.new) return a.name.localeCompare(b.name)
      return 0
    })
    setOptions(sortedOptions)
  }

  const onAddOption = () => {
    sortOptions([...options, { name: '', new: true }])
  }

  const handleInputChange = (index: number, value: string) => {
    const updatedOptions = options.map((option, i) =>
      i === index ? { ...option, name: value } : option
    )
    sortOptions(updatedOptions)
  }

  const onArchiveOption = (index: number) => {
    const updatedOptions = options.map((option, i) =>
      i === index
        ? { ...option, archivedAt: !!option.archivedAt ? null : new Date(), edited: true }
        : option
    )
    sortOptions(updatedOptions)
  }

  const onDeleteOption = (index: number) => {
    const updatedOptions = options.filter((_option, i) => i !== index)
    sortOptions(updatedOptions)
  }

  const handleOnSubmit = () => {
    performAction()
    setOpenModal(false)
  }

  const { performAction, running } = useAsyncAction(
    async () => {
      const editedOptions = options.filter((option) => option.edited)
      const newOptions = options.filter((option) => option.new)
      const archivePromises = editedOptions.map((option) => {
        if (!option.id) return
        return archiveTaxonomies({
          variables: { ...mutationVariables, id: option.id, archived: !!option.archivedAt },
        })
      })
      const createPromises = newOptions.map((option) => {
        return createTaxonomies({
          variables: {
            ...mutationVariables,
            name: option.name,
            contractsTemplateId: contractsTemplateId,
            legalEntityId: legalEntityId || -1,
          },
        })
      })

      await Promise.all([...archivePromises, ...createPromises])
    },
    {
      successFlash: true,
      successFlashTime: 1000,
      loadingMessage: i18n.t('common.saving'),
    }
  )

  return (
    <>
      <BoxList.Item>
        <Box flexDirection="row" alignItems="center" width="full" justifyContent="spaceBetween">
          <Box flexDirection="row" gap="s16">
            <IconBadge size="s24" icon="circle" iconColor="viridian1200" color="viridian200" />
            <Text.Normal size="300" weight="medium" color="grey1000">
              {name}
            </Text.Normal>
          </Box>
          <Box gap="s16" flexDirection="row" alignItems="center" justifyContent="flexEnd">
            {!!tag && (
              <Tooltip.Alt attachment="bottom center" open={open} toggle={toggle} label={tag.hover}>
                <Tag type="grey" size="normal" leftItem="icon" icon={tag.icon} iconColor="grey800">
                  {tag.label}
                </Tag>
              </Tooltip.Alt>
            )}
            <Button type="secondary" iconName="arrow-right" onClick={() => setOpenModal(true)} />
          </Box>
        </Box>
      </BoxList.Item>
      {openModal && (
        <EditFieldModal
          fieldName={name}
          options={options}
          onClose={() => setOpenModal(false)}
          onAddOption={() => onAddOption()}
          handleInputChange={handleInputChange}
          onArchiveOption={onArchiveOption}
          onDeleteOption={onDeleteOption}
          onSubmit={handleOnSubmit}
          submitting={running}
        />
      )}
    </>
  )
}

export default ContractField
